// ----------------------------------------------------------------------
// <copyright file="Izenda.AccountStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var AccountInfo = require('logic/models/Izenda.AccountInfo');
  var SecurityQuestionReadOnly = require('logic/models/Izenda.SecurityQuestionReadOnly');
  var fetcher = require('fetchers/Izenda.UserAccountDataFetcher');
  var exportFetcher = require('fetchers/Izenda.ExportFetcher');
  var systemSettingFetcher = require('fetchers/Izenda.SystemSettingFetcher');
  var notificationFetcher = require('fetchers/Izenda.NotificationFetcher');
  var AccountConstants = require('logic/data/Izenda.AccountConstants');
  var uiState = require('logic/Izenda.UiState');
  var MyProfileMenuStore = require('./Izenda.MyProfileMenuStore');
  var AlertDetails = require('logic/models/Izenda.AlertDetails');

  var SecurityManager = require('share/authorization/Izenda.SecurityManager');
  const { find, isEmpty, map, trim, pick, mapValues } = require('lodash/fp');

  function AccountStore(mode) {
    var props = {
      model: new AccountInfo(mode),
      tenantId: function() {
        return uiState.getTenantId();
      },
      tenantDisplayId: function() {
        return uiState.currentUser.tenantDisplayID;
      },
      userName: function() {
        return uiState.currentUser.userName;
      },
      alertDetails: new AlertDetails()
    };

    this.contextMenu = new MyProfileMenuStore();
    m.extendObservable(this, props);
  }

  /**
   * Init Account Store
   */
  AccountStore.prototype.init = function(systemConfig) {
    uiState.setCheckDirtyFunction(
      function() {
        return this.model.isDirty;
      }.bind(this)
    );
  };

  /**
   * Destroy Account Store
   */
  AccountStore.prototype.destroy = function(systemConfig) {
    uiState.resetCheckDirtyFunction();
  };

  /**
   * Populate model info
   * @param  {String} tenantId
   * @param  {String} verification
   */
  AccountStore.prototype.populateTenantInfo = function() {
    return this.populateTenantConfig()
      .bind(this)
      .then(
        m.action(function(result) {
          return this.model.populateSystemConfig(result);
        })
      );
  };

  /**
   * Load security questions of user
   * @param  {Array} questions
   */
  AccountStore.prototype.loadUserSecurityQuestions = function(questions) {
    return this.populateTenantInfo()
      .bind(this)
      .then(function() {
        return this.model.populateUserSecurityQuestions(questions);
      });
  };

  /**
   * Validate expiration of password link.
   * @param  {String} verification
   */
  AccountStore.prototype.validateExpirationPasswordLink = function(verification) {
    return fetcher.validateExpirationPasswordLink({
      verification: verification
    });
  };

  /**
   * Validate password link.
   */
  AccountStore.prototype.validatePasswordLink = m.action(function() {
    // Trim data for validate
    var request = mapValues(
      trim,
      pick(['tenantName', 'userName', 'firstName', 'lastName', 'emailAddress', 'verification'], this.model)
    );

    var fn = this.model.verification ? fetcher.validatePasswordLink : fetcher.validateUserInfo;

    return fn(request)
      .bind(this)
      .then(function(result) {
        if (result.success) {
          this.model.updateProperty('tenantDisplayID', result.data.tenantDisplayId);
          switch (this.model.mode) {
            case AccountConstants.MODES.PASSWORD_SETUP:
              return Promise.join([
                this.model.updateCurrentStep(AccountConstants.STEP_KEYS.SecurityInfo),
                this.loadUserSecurityQuestions()
              ]);
            case AccountConstants.MODES.FORGOT_PASSWORD:
            case AccountConstants.MODES.RESET_PASSWORD:
              this.model.id = result.data.id;
              return Promise.join(
                this.model.updateCurrentStep(AccountConstants.STEP_KEYS.QuestionInfo),
                this.loadSecurityQuestionsOfUser(),
                function(obj, questionsResult) {
                  return this.loadUserSecurityQuestions(questionsResult.data);
                }.bind(this)
              );
          }
        }
        this.model.updateProperty('message', AccountConstants.MESSAGES.M01);
        return result;
      });
  });

  /**
   * Active account.
   */
  AccountStore.prototype.passwordAndSecurityQuestion = function() {
    var request = pick(
      [
        'tenantDisplayID',
        'tenantName',
        'password',
        'verification',
        'userName',
        'firstName',
        'lastName',
        'emailAddress'
      ],
      this.model
    );
    request.userSecurityQuestions = map(function(item) {
      return item.buildDataForSaving();
    }, this.model.userSecurityQuestions);
    request.autoLogin = true;

    return fetcher.passwordAndSecurityQuestion(request);
  };

  /**
   * Load security questions.
   */
  AccountStore.prototype.loadSecurityQuestions = function() {
    var request = pick(['tenantDisplayID', 'tenantName', 'userName'], this.model);
    return systemSettingFetcher.securityQuestions(request).then(function(result) {
      return map(function(item) {
        return new SecurityQuestionReadOnly(item);
      }, result);
    });
  };

  /**
   * Build tenant config info.
   * @param  {String} tenantId
   */
  AccountStore.prototype.populateTenantConfig = function() {
    return Promise.join(this.loadSecurityQuestions(), this.loadSystemConfig(), function(questions, config) {
      return {
        fullSecurityQuestions: questions,
        numberOfQuestionProfile: config.numberOfQuestionProfile
      };
    });
  };

  /**
   * Login.
   */
  AccountStore.prototype.login = function() {
    var request = pick(['tenantName', 'password', 'userName'], this.model);
    return fetcher.login(request);
  };

  /**
   * Logout.
   */
  AccountStore.prototype.logout = function() {
    return fetcher.logout();
  };

  /**
   * Load security question of user.
   */
  AccountStore.prototype.loadSecurityQuestionsOfUser = function() {
    var request = pick(['tenantDisplayID', 'userName'], this.model);
    return fetcher.securityQuestion(request);
  };

  /**
   * Validate security question of user.
   */
  AccountStore.prototype.validateSecurityQuestion = function() {
    var request = pick(['tenantDisplayID', 'userName'], this.model);
    request.userSecurityQuestions = this.model.buildUserSecurityQuestionsForSaving();
    return fetcher.validateSecurityQuestion(request);
  };

  /**
   * Update password info of user.
   */
  AccountStore.prototype.updatePassword = function() {
    var request = pick(['tenantDisplayID', 'tenantName', 'password', 'userName', 'id'], this.model);
    request.userSecurityQuestions = map(function(item) {
      return item.buildDataForSaving();
    }, this.model.userSecurityQuestions);
    request.autoLogin = true;
    return fetcher.password(request);
  };

  /**
   * Update account info after checked password link.
   * @param  {String} verification
   * @param  {Boolean} isExpired
   */
  AccountStore.prototype.updateInfoAfterCheckedPasswordLink = m.action(function(verification, isExpired) {
    this.model.updateProperty('verification', verification);
    this.model.updateProperty('isExpiration', isExpired);
  });

  /**
   * Load general error message.
   * @param  {Array} messages
   */
  AccountStore.prototype.loadGeneralErrorMessage = function(messages) {
    var message = find(function(item) {
      return isEmpty(item.key);
    }, messages);

    if (message) {
      this.model.updateProperty('message', message.messages);
    }
  };

  /**
   * Load system config info.
   * @param  {String} tenantId
   */
  AccountStore.prototype.loadSystemConfig = function() {
    return systemSettingFetcher.loadSecurityPolicies();
  };

  /**
   * Check database setup.
   */
  AccountStore.prototype.checkDatabaseSetup = function() {
    return systemSettingFetcher.databaseSetup();
  };

  /**
   * Load System Mode
   */
  AccountStore.prototype.loadSystemMode = function() {
    return systemSettingFetcher.loadSystemModeSettings();
  };

  /**
   * Load information of current user.
   */
  AccountStore.prototype.loadCurrentUser = function() {
    return fetcher.loadCurrentUser();
  };

  /**
   * Load export queue list
   */
  AccountStore.prototype.loadExportQueueList = function() {
    var pageInfo = {
      pageSize: this.model.exportPagingInfo.pageSize,
      pageIndex: this.model.exportPagingInfo.pageIndex
    };
    var userId = uiState.currentUser.id;

    return exportFetcher.getExportQueueList(userId, pageInfo).then(exportQueues => {
      this.model.exportList = exportQueues.result;
      this.model.exportTotalRecords = exportQueues.total;
      this.model.exportPagingInfo.totalRecords = exportQueues.total;
    });
  };

  /**
   * Go to export manager page
   */
  AccountStore.prototype.goToExportManagerPage = m.action(function(pageIndex) {
    this.model.exportPagingInfo.setPageIndex(pageIndex);
    return this.loadExportQueueList();
  });

    /**
   * getNotificationInterval
   */
  AccountStore.prototype.getNotificationInterval = function() {
    return systemSettingFetcher.getNotificationInterval();
  };

  /**
   * markNotificationsAsRead
   */
  AccountStore.prototype.markNotificationsAsRead = function() {
    return notificationFetcher.markNotificationsAsRead();
  };

  /**
   * Download export queue file
   */
  AccountStore.prototype.downloadExportQueueFile = function(data) {
    return exportFetcher.getExportQueueFile(data);
  };

  /**
   * delete export queue file
   */
  AccountStore.prototype.deleteExportQueueFile = function(data) {
    return exportFetcher.deleteExportQueueFile(data);
  }

  /**
   * Get my preference information
   */
  AccountStore.prototype.getMyProfile = function() {
    var securityQuestionsRequest = {
      tenantDisplayID: this.tenantDisplayId,
      userName: this.userName
    };

    return Promise.join(
      this.loadCurrentUser(),
      systemSettingFetcher.loadAllLanguages(),
      systemSettingFetcher.loadAllDateFormat(),
      systemSettingFetcher.securityQuestions(securityQuestionsRequest),
      this.loadSystemConfig(),
      function(currentUser, languageList, dateFormatList, questions, config) {
        this.model.populateMyProfile(currentUser, languageList, dateFormatList, questions, config);
      }.bind(this)
    );
  };

  /**
   * save my profile
   */
  AccountStore.prototype.save = function() {
    var data = this.model.buildDataForSaving();
    var hasChangeLanguage = data.hasChangeLanguage;

    return fetcher
      .saveUserProfile(data)
      .bind(this)
      .then(
        m.action(function(result) {
          if (result.success) {
            this.model.populate(result.data);
            // Update for current user which uses for global system.
            uiState.currentUser.populate(result.data);
            // Reset dirty
            this.model.resetDirty();

            result.hasChangeLanguage = hasChangeLanguage;
            return Promise.resolve(result);
          } else {
            return result;
          }
        })
      );
  };

  /**
   * Update new password
   * @param {String} current password
   * @param {String} new password
   */
  AccountStore.prototype.updateNewPassword = function(currentPassword, newPassword) {
    var data = {
      newPassword: newPassword,
      password: currentPassword,
      userName: this.model.userName,
      Id: this.model.id
    };
    return fetcher
      .updateNewPassword(data)
      .bind(this)
      .then(function(result) {
        if (result.success) {
          new SecurityManager().setAccessToken(result.data.token);
          return Promise.resolve(true);
        } else {
          return result;
        }
      });
  };

  /**
   * Update security questions
   * @param {String} current password
   * @param {String} new password
   * returns {Object} promise
   */
  AccountStore.prototype.updateSecurityQuesitions = function(currentPassword, newPassword) {
    var data = this.model.buildForUpdateSecurityQuestions();
    return fetcher
      .updateSecurityQuesitions(data)
      .bind(this)
      .then(function(result) {
        if (result.success) {
          this.model.resetAnswers();
          this.loadCurrentUser()
            .bind(this)
            .then(function(result) {
              this.model.populate(result.data);
            });
          return Promise.resolve(true);
        } else {
          return result;
        }
      });
  };

  module.exports = AccountStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.AccountStore.js